import {
  type CommentResolutionFilterOption,
  type CommentVisibilityFilterOption,
  changeNodeCommentResolutionSchema,
  createNodeCommentSchema,
  getCommentRepliesSchema,
  getNodeCommentsSchema,
} from '@/types/comments';
import { MAX_COMMENT_CHARACTERS, normalizeCommentBody } from '@/lib/comments';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { INTERNAL_V1, damRequest } from '@/hooks/damRequest';
import { t } from '@/i18n/translator';

const validateCommentBody = (body: string) => {
  const normalizedBody = normalizeCommentBody(body);

  if (!normalizedBody.trim()) {
    throw new Error(t('inspector.comments.commentCannotBeEmpty'));
  }

  if (normalizedBody.length > MAX_COMMENT_CHARACTERS) {
    throw new Error(
      t('inspector.comments.commentCannotExceedCharacters', {
        max: MAX_COMMENT_CHARACTERS,
      }),
    );
  }

  return normalizedBody;
};

export const getNodeComments = async (
  {
    nodeId,
    limit = 10,
    offset = 0,
    sortBy = SortOrderValues.CREATED_AT,
    sortOrder = SortOrderOptions.ASC,
    selected,
    shareId,
    filter,
  }: {
    nodeId: string;
    limit: number;
    offset: number;
    sortBy: string;
    sortOrder: string;
    selected?: string;
    shareId?: string;
    filter?: {
      resolution?: CommentResolutionFilterOption;
      visibility?: CommentVisibilityFilterOption;
    };
  },
  token?: string,
) => {
  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    sortBy: sortBy,
    sortOrder: sortOrder,
  });

  if (selected) {
    query.set('selected', selected);
  }

  if (filter?.resolution) {
    query.set('filter[resolution]', filter.resolution);
  }

  if (filter?.visibility) {
    query.set('filter[visibility]', filter.visibility);
  }

  // If shareId is present, hit the share comment endpoint. Everything else stays the same for simplicity
  const url = shareId ? `/nodes/${nodeId}/shares/${shareId}/comments` : `/nodes/${nodeId}/comments`;

  const response = await damRequest('GET', `${INTERNAL_V1}${url}?${query.toString()}`, token);
  return getNodeCommentsSchema.parse(response);
};

export const getCommentReplies = async (
  {
    commentId,
    limit = 500,
    offset = 0,
    shareId,
  }: {
    commentId: string;
    limit?: number;
    offset?: number;
    shareId?: string;
  },
  token?: string,
) => {
  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (shareId) {
    query.set('shareId', shareId);
  }

  // If shareId is present, hit the share comment endpoint. Everything else stays the same for simplicity
  const url = shareId ? `/comments/${commentId}/shares/${shareId}/replies` : `/comments/${commentId}/replies`;

  const response = await damRequest('GET', `${INTERNAL_V1}${url}?${query.toString()}`, token);
  return getCommentRepliesSchema.parse(response);
};

export const createNodeComment = async (
  {
    nodeId,
    body,
    parentId,
    private: isPrivate = false,
  }: {
    nodeId: string;
    body: string;
    parentId?: string;
    private?: boolean;
  },
  token?: string,
) => {
  const normalizedBody = validateCommentBody(body);

  const response = await damRequest('POST', `${INTERNAL_V1}/nodes/${nodeId}/comments`, token, {
    body: { body: normalizedBody, parentId: parentId ?? null, private: isPrivate },
  });

  return createNodeCommentSchema.parse(response);
};

export const updateNodeComment = async (
  {
    commentId,
    body,
    private: isPrivate = false,
  }: {
    commentId: string;
    body: string;
    private?: boolean;
  },
  token?: string,
) => {
  const normalizedBody = validateCommentBody(body);

  const response = await damRequest('PATCH', `${INTERNAL_V1}/comments/${commentId}`, token, {
    body: { body: normalizedBody, private: isPrivate },
  });

  return createNodeCommentSchema.parse(response);
};

export const deleteNodeComment = ({ id }: { id: string }, token?: string) =>
  damRequest('DELETE', `${INTERNAL_V1}/comments/${id}`, token);

export const changeNodeCommentResolution = async (
  {
    commentId,
    resolve,
  }: {
    commentId: string;
    resolve: boolean;
  },
  token?: string,
) => {
  const response = await damRequest('PATCH', `${INTERNAL_V1}/threads/${commentId}`, token, {
    body: { resolve },
  });
  return changeNodeCommentResolutionSchema.parse(response);
};
